<template>
  <div class="robot-globe" ref="container">
    <!-- 平行光束 -->
    <div class="parallel-beams">
      <div class="beam left"></div>
      <div class="beam right"></div>
    </div>

    <!-- 加载状态 -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loader"></div>
      <div class="loading-text">{{ langMap.robotGlobe_loading[lang] }}</div>
    </div>

    <!-- 错误状态 -->
    <div v-if="loadError" class="error-overlay">
      <div class="error-container">
        <div class="error-message">{{ errorMsg }}</div>
        <button class="retry-btn" @click="retryLoad">{{ langMap.robotGlobe_retryLoad[lang] }}</button>
      </div>
    </div>

    <div class="canvas-container" ref="canvasContainer" @wheel="handleWheel" :class="{ 'loaded': !isLoading }">
      <div class="floating-wrapper" ref="floatingWrapper">
        <!-- 球体将在这里渲染 -->
      </div>
      <div v-if="ballTemperatureDisplay !== '--'" class="ball-temp-callout">
        <svg
          class="callout-wire"
          :viewBox="`0 0 ${wireSvgSize.width} ${wireSvgSize.height}`"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <polyline
            v-if="wirePoints"
            class="wire-path"
            :points="wirePoints"
            fill="none"
            vector-effect="non-scaling-stroke"
          />
          <circle
            v-if="wirePoints"
            class="wire-anchor-glow"
            :cx="wireAnchor.x"
            :cy="wireAnchor.y"
            r="8"
            vector-effect="non-scaling-stroke"
          />
          <circle
            v-if="wirePoints"
            class="wire-anchor"
            :cx="wireAnchor.x"
            :cy="wireAnchor.y"
            r="3"
            vector-effect="non-scaling-stroke"
          />
        </svg>
        <div class="callout-panel" ref="calloutPanel">
          <div class="callout-title">{{ ballTemperatureLabel }}</div>
          <div class="callout-value">{{ ballTemperatureDisplay }}<span class="unit">{{ ballTemperatureUnit }}</span></div>
        </div>
      </div>
      <!-- 科幻三角形底座 -->
      <div class="base">
        <!-- 发光能量柱 -->
        <div class="energy-column">
          <div class="energy-beam"></div>
        </div>
        <!-- 三角形底座 -->
        <div class="base-platform">
          <div class="triangle front"></div>
          <div class="triangle left"></div>
          <div class="triangle right"></div>
          <!-- 底座装饰线条 -->
          <div class="tech-lines">
            <div class="line"></div>
            <div class="line"></div>
            <div class="line"></div>
          </div>
        </div>
        <!-- 底部光晕 -->
        <div class="base-glow"></div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three'
import { gsap } from 'gsap'
import qiuTexture from '@/assets/images/dashboard/Rotunbot.png';
import langMap from '@/utils/language'
import { mapState } from 'vuex'

export default {
  name: 'RobotGlobe',
  props: {
    robotData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      scene: null,
      camera: null,
      renderer: null,
      robot: null,
      animation: null,
      isInitialized: false,
      isLoading: true,
      loadError: null,
      errorMsg: null,
      resizeObserver: null,
      langMap,
      scale: {
        current: 1,
        min: 0.3,
        max: 3,
        speed: 0.15
      },
      wireSvgSize: { width: 1, height: 1 },
      wirePoints: '',
      wireAnchor: { x: 0, y: 0 },
      calloutResizeObserver: null
    }
  },
  computed: {
    ...mapState({
      lang: state => state.app.language
    }),
    ballTemperatureRaw() {
      return this.robotData && this.robotData.ballTemperature
    },
    ballTemperatureUnit() {
      return (this.robotData && this.robotData.ballTemperatureUnit) || '℃'
    },
    ballTemperatureLabel() {
      if (!this.robotData) {
        return ''
      }
      return this.lang === 'zh'
        ? (this.robotData.ballTemperatureNameCn || this.robotData.ballTemperatureNameEn || '')
        : (this.robotData.ballTemperatureNameEn || this.robotData.ballTemperatureNameCn || '')
    },
    ballTemperatureDisplay() {
      const value = this.ballTemperatureRaw
      if (value === null || value === undefined || value === '') {
        return '--'
      }

      const numValue = typeof value === 'number' ? value : parseFloat(value)
      if (Number.isNaN(numValue)) {
        return value
      }
      return numValue.toFixed(2)
    }
  },
  watch: {
    ballTemperatureDisplay(val) {
      if (val !== '--') {
        this.$nextTick(() => {
          this.updateCalloutWire()
          this.observeCalloutLayout()
        })
      } else {
        this.unobserveCalloutLayout()
      }
    },
    isLoading(val) {
      if (!val) {
        this.$nextTick(this.updateCalloutWire)
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initThree()
      this.startFloatingAnimation()
    })
  },
  methods: {
    updateCalloutWire() {
      const container = this.$refs.canvasContainer
      const panel = this.$refs.calloutPanel
      const floater = this.$refs.floatingWrapper
      if (!container || !panel || !floater || this.ballTemperatureDisplay === '--') {
        return
      }

      const cRect = container.getBoundingClientRect()
      const pRect = panel.getBoundingClientRect()
      const fRect = floater.getBoundingClientRect()
      if (!cRect.width || !cRect.height) {
        return
      }

      this.wireSvgSize = {
        width: Math.round(cRect.width),
        height: Math.round(cRect.height)
      }

      // 以 Three.js 画布实际边界作为球体边缘
      let ballRect = fRect
      const canvasEl = this.renderer && this.renderer.domElement
      if (canvasEl) {
        const canvasBox = canvasEl.getBoundingClientRect()
        if (canvasBox.width > 0 && canvasBox.height > 0) {
          ballRect = canvasBox
        }
      }
      // 球体可见右缘（避免透明边距导致拐点偏左）
      const anchorX = ballRect.left + ballRect.width * 0.72 - cRect.left //锚点 X：球体宽度的比例。越大越靠右（0.9→1.0 贴右缘）
      const anchorY = ballRect.top + ballRect.height * 0.65 - cRect.top //锚点 Y：球体高度的比例。越大越靠下（0.52→0.62 抬高）
      const panelCenterX = pRect.left + pRect.width / 2 - cRect.left //卡片中心 X：卡片左边缘到画布左边缘的距离
      const panelTopY = pRect.top - cRect.top //卡片顶部中心 Y：卡片顶部边缘到画布顶部边缘的距离

      this.wireAnchor = {
        x: Math.round(anchorX),
        y: Math.round(anchorY)
      }

      const cornerX = Math.round(panelCenterX)
      const endY = Math.round(panelTopY)

      // 从球边缘先水平到卡片中心 X，再垂直到卡片顶部中心
      if (panelCenterX >= anchorX) {
        this.wirePoints = [
          `${this.wireAnchor.x},${this.wireAnchor.y}`,
          `${cornerX},${this.wireAnchor.y}`,
          `${cornerX},${endY}`
        ].join(' ')
      } else {
        // 卡片在球左缘内侧时，先垂直到卡片高度再水平到中心
        this.wirePoints = [
          `${this.wireAnchor.x},${this.wireAnchor.y}`,
          `${this.wireAnchor.x},${endY}`,
          `${cornerX},${endY}`
        ].join(' ')
      }
    },
    observeCalloutLayout() {
      if (this.calloutResizeObserver || !this.$refs.canvasContainer) {
        return
      }

      this.calloutResizeObserver = new ResizeObserver(() => {
        this.updateCalloutWire()
      })
      this.calloutResizeObserver.observe(this.$refs.canvasContainer)
      if (this.$refs.floatingWrapper) {
        this.calloutResizeObserver.observe(this.$refs.floatingWrapper)
      }
      if (this.$refs.calloutPanel) {
        this.calloutResizeObserver.observe(this.$refs.calloutPanel)
      }
    },
    unobserveCalloutLayout() {
      if (this.calloutResizeObserver) {
        this.calloutResizeObserver.disconnect()
        this.calloutResizeObserver = null
      }
    },
    retryLoad() {
      this.loadError = null
      this.isLoading = true
      this.isInitialized = false

      // 清理旧的 renderer
      if (this.renderer) {
        if (this.renderer.domElement && this.renderer.domElement.parentNode) {
          this.renderer.domElement.parentNode.removeChild(this.renderer.domElement)
        }
        this.renderer.dispose()
        this.renderer = null
      }

      this.scene = null
      this.camera = null
      this.robot = null

      this.initThree()
    },
    initThree() {
      try {
        if (!this.$refs.floatingWrapper || !this.$refs.container) {
          console.warn('容器引用未就绪')
          return
        }

        this.scene = new THREE.Scene()
        this.renderer = new THREE.WebGLRenderer({
          antialias: true,
          alpha: true,
          preserveDrawingBuffer: true
        })

        this.renderer.setPixelRatio(window.devicePixelRatio)
        this.renderer.setClearColor(0x000000, 0)
        this.$refs.floatingWrapper.appendChild(this.renderer.domElement)

        const textureLoader = new THREE.TextureLoader()
        textureLoader.load(
          qiuTexture,
          this.setupScene,
          undefined,
          (error) => {
            console.error('纹理加载失败:', error)
            this.loadError = true
            this.errorMsg = langMap.robotGlobe_loadFailed[this.lang]
            this.isLoading = false
          }
        )
      } catch (error) {
        console.error('Three.js 初始化失败:', error)
        this.loadError = true
        this.errorMsg = langMap.robotGlobe_initFailed[this.lang]
        this.isLoading = false
      }
    },
    setupScene(texture) {
      try {
        if (!this.$refs.container) {
          throw new Error('容器引用丢失')
        }

        const containerRect = this.$refs.container.getBoundingClientRect()
        const containerWidth = containerRect.width
        const containerHeight = containerRect.height

        if (containerWidth === 0 || containerHeight === 0) {
          console.warn('容器尺寸为0，延迟初始化')
          setTimeout(() => this.setupScene(texture), 100)
          return
        }

        this.renderer.setSize(containerWidth, containerHeight)

        const geometry = new THREE.PlaneGeometry(1, 1)
        const material = new THREE.MeshBasicMaterial({
          map: texture,
          transparent: true,
          side: THREE.DoubleSide
        })

        this.robot = new THREE.Mesh(geometry, material)
        this.scene.add(this.robot)

        this.setupCamera(containerWidth, containerHeight)
        this.updateSizeAndCamera(containerWidth, containerHeight, texture.image.width / texture.image.height)

        this.isInitialized = true
        this.isLoading = false
        this.animate()
        this.$nextTick(() => {
          this.updateCalloutWire()
          if (this.ballTemperatureDisplay !== '--') {
            this.observeCalloutLayout()
          }
        })

        this.resizeObserver = new ResizeObserver(this.onContainerResize)
        this.resizeObserver.observe(this.$refs.container)
      } catch (error) {
        console.error('场景设置失败:', error)
        this.loadError = true
        this.errorMsg = langMap.robotGlobe_initFailed[this.lang]
        this.isLoading = false
      }
    },
    startFloatingAnimation() {
      gsap.to(this.$refs.floatingWrapper, {
        y: 20,
        duration: 1.5,
        yoyo: true,
        repeat: -1,
        ease: "power1.inOut",
        onUpdate: () => this.updateCalloutWire()
      })
    },
    handleWheel(event) {
      event.preventDefault()

      if (!this.robot) return

      const delta = event.deltaY < 0 ? 1 : -1
      const scaleFactor = 1 + (delta * this.scale.speed)

      const newScale = this.scale.current * scaleFactor

      if (newScale >= this.scale.min && newScale <= this.scale.max) {
        this.scale.current = newScale
        const currentY = this.robot.position.y
        this.robot.scale.x *= scaleFactor
        this.robot.scale.y *= scaleFactor
        this.robot.position.y = currentY
      }
    },
    setupCamera(width, height) {
      const frustumSize = Math.max(width, height)
      const aspect = width / height

      this.camera = new THREE.OrthographicCamera(
        -frustumSize * aspect / 2,
        frustumSize * aspect / 2,
        frustumSize / 2,
        -frustumSize / 2,
        1,
        1000
      )
      this.camera.position.z = 5
    },
    updateSizeAndCamera(containerWidth, containerHeight, imageAspect) {
      let planeWidth, planeHeight
      const padding = 40

      if (containerWidth / containerHeight > imageAspect) {
        planeHeight = containerHeight - padding
        planeWidth = planeHeight * imageAspect

        if (planeWidth > (containerWidth - padding)) {
          planeWidth = containerWidth - padding
          planeHeight = planeWidth / imageAspect
        }
      } else {
        planeWidth = containerWidth - padding
        planeHeight = planeWidth / imageAspect

        if (planeHeight > (containerHeight - padding)) {
          planeHeight = containerHeight - padding
          planeWidth = planeHeight * imageAspect
        }
      }

      if (this.robot) {
        const currentY = this.robot.position.y
        this.robot.scale.set(planeWidth, planeHeight, 1)
        this.robot.position.y = currentY
      }

      const frustumSize = Math.max(containerWidth, containerHeight)
      const aspect = containerWidth / containerHeight

      if (this.camera) {
        this.camera.left = -frustumSize * aspect / 2
        this.camera.right = frustumSize * aspect / 2
        this.camera.top = frustumSize / 2
        this.camera.bottom = -frustumSize / 2
        this.camera.updateProjectionMatrix()
      }

      this.renderer.setSize(containerWidth, containerHeight)
    },
    animate() {
      if (!this.isInitialized) return

      requestAnimationFrame(this.animate)
      if (this.scene && this.camera && this.renderer) {
        this.renderer.render(this.scene, this.camera)
      }
    },
    onContainerResize(entries) {
      if (!this.isInitialized) return

      const entry = entries[0]
      if (entry) {
        const containerWidth = entry.contentRect.width
        const containerHeight = entry.contentRect.height

        if (this.robot && this.robot.material.map) {
          const imageAspect = this.robot.material.map.image.width / this.robot.material.map.image.height
          this.updateSizeAndCamera(containerWidth, containerHeight, imageAspect)
        }
        this.updateCalloutWire()
      }
    }
  },
  beforeDestroy() {
    this.unobserveCalloutLayout()
    if (this.animation) {
      this.animation.kill()
    }
    if (this.renderer) {
      this.renderer.dispose()
      if (this.renderer.domElement && this.renderer.domElement.parentNode) {
        this.renderer.domElement.parentNode.removeChild(this.renderer.domElement)
      }
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  }
}
</script>

<style lang="less" scoped>
.robot-globe {
  width: 100%;
  height: 100%;
  min-height: 300px;
  position: relative;
  overflow: hidden;

  // 加载状态插层
  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 100;

    .loader {
      width: 50px;
      height: 50px;
      border: 3px solid rgba(0, 150, 255, 0.2);
      border-top-color: rgba(0, 150, 255, 0.8);
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: 20px;
    }

    .loading-text {
      color: #0096ff;
      font-size: 14px;
      letter-spacing: 2px;
    }
  }

  // 错误状态插层
  .error-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 100;

    .error-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 20px;
    }

    .error-message {
      color: #ff4444;
      font-size: 16px;
      text-align: center;
      padding: 20px;
      max-width: 300px;
    }

    .retry-btn {
      padding: 10px 30px;
      background-color: #0096ff;
      color: white;
      border: none;
      border-radius: 4px;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background-color: #0078cc;
        box-shadow: 0 0 15px rgba(0, 150, 255, 0.5);
      }

      &:active {
        transform: scale(0.95);
      }
    }
  }

  .canvas-container {
    position: relative;
    width: 100%;
    height: 100%;
    opacity: 0;
    transition: opacity 0.5s ease-in-out;

    &.loaded {
      opacity: 1;
    }

    canvas {
      display: block;
    }

    .floating-wrapper {
      position: absolute;
      top: 45%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 80%;
      height: 80%;
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 2;

      canvas {
        max-width: 100%;
        max-height: 100%;
      }
    }

    .ball-temp-callout {
      position: absolute;
      inset: 0;
      z-index: 4;
      pointer-events: none;

      .callout-wire {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        overflow: visible;
        z-index: 1;
        pointer-events: none;

        .wire-path {
          stroke: rgba(0, 150, 255, 0.5);
          stroke-width: 2px;
          stroke-linecap: round;
          stroke-linejoin: round;
          filter: drop-shadow(0 0 10px rgba(0, 150, 255, 0.5));
        }

        .wire-anchor-glow {
          fill: rgba(0, 150, 255, 0.45);
          stroke: none;
          animation: anchor-glow-pulse 2s ease-in-out infinite;
        }

        .wire-anchor {
          fill: rgba(0, 200, 255, 0.95);
          stroke: rgba(0, 240, 255, 0.9);
          stroke-width: 1px;
          animation: anchor-core-pulse 2s ease-in-out infinite;
        }
      }

      .callout-panel {
        position: absolute;
        right: 6%;
        bottom: 18%;
        min-width: 132px;
        z-index: 2;
        padding: 8px 12px;
        border: 1px solid rgba(0, 200, 255, 0.55);
        border-radius: 6px;
        background: linear-gradient(135deg, rgba(0, 35, 60, 0.85), rgba(0, 10, 25, 0.88));
        box-shadow: 0 0 16px rgba(0, 170, 255, 0.35), inset 0 0 10px rgba(0, 170, 255, 0.2);

        .callout-title {
          font-size: 10px;
          letter-spacing: 1px;
          color: rgba(175, 235, 255, 0.85);
          text-transform: uppercase;
          margin-bottom: 4px;
        }

        .callout-value {
          color: #ffffff;
          font-size: 18px;
          font-weight: 700;
          line-height: 1;
          text-shadow: 0 0 10px rgba(0, 225, 255, 0.5);

          .unit {
            margin-left: 2px;
            font-size: 11px;
            color: #6edfff;
          }
        }
      }
    }

    // 底座样式
    .base {
      position: absolute;
      bottom: 5%;
      left: 50%;
      transform: translateX(-50%);
      width: 300px;
      height: 80px;
      perspective: 1000px;

      // 能量柱
      .energy-column {
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        width: 80px;
        height: 200px;
        display: flex;
        justify-content: center;
        opacity: 0.7;

        .energy-beam {
          width: 2px;
          height: 100%;
          background: linear-gradient(
            to top,
            rgba(0, 150, 255, 1),
            rgba(0, 150, 255, 0)
          );
          box-shadow:
            0 0 10px rgba(0, 150, 255, 0.5),
            0 0 20px rgba(0, 150, 255, 0.3),
            0 0 30px rgba(0, 150, 255, 0.1);
          animation: energy-pulse 2s ease-in-out infinite;
        }
      }

      // 三角形底座
      .base-platform {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 100%;
        transform-style: preserve-3d;
        animation: base-rotate 20s linear infinite;

        .triangle {
          position: absolute;
          width: 100%;
          height: 100%;
          background: rgba(0, 150, 255, 0.1);
          border: 2px solid rgba(0, 150, 255, 0.5);
          backdrop-filter: blur(5px);
          clip-path: polygon(50% 0%, 0% 100%, 100% 100%);

          &.front {
            transform: translateZ(40px);
          }

          &.left {
            transform: rotateY(120deg) translateZ(40px);
          }

          &.right {
            transform: rotateY(-120deg) translateZ(40px);
          }
        }

        // 装饰线条
        .tech-lines {
          position: absolute;
          width: 100%;
          height: 100%;

          .line {
            position: absolute;
            bottom: 20%;
            left: 10%;
            width: 80%;
            height: 2px;
            background: rgba(0, 150, 255, 0.5);
            box-shadow: 0 0 10px rgba(0, 150, 255, 0.5);

            &:nth-child(2) {
              bottom: 40%;
              left: 20%;
              width: 60%;
            }

            &:nth-child(3) {
              bottom: 60%;
              left: 30%;
              width: 40%;
            }
          }
        }
      }

      // 底部光晕
      .base-glow {
        position: absolute;
        bottom: -20px;
        left: 50%;
        transform: translateX(-50%);
        width: 250px;
        height: 40px;
        background: radial-gradient(
          ellipse at center,
          rgba(0, 150, 255, 0.4) 0%,
          rgba(0, 150, 255, 0) 70%
        );
        filter: blur(10px);
        animation: glow-pulse 2s ease-in-out infinite;
      }
    }
  }
}

@keyframes energy-pulse {
  0%, 100% {
    opacity: 0.7;
    height: 180px;
  }
  50% {
    opacity: 0.9;
    height: 200px;
  }
}

@keyframes base-rotate {
  from {
    transform: rotateY(0deg);
  }
  to {
    transform: rotateY(360deg);
  }
}

@keyframes glow-pulse {
  0%, 100% {
    opacity: 0.4;
    transform: translateX(-50%) scale(1);
  }
  50% {
    opacity: 0.6;
    transform: translateX(-50%) scale(1.1);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes anchor-glow-pulse {
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.85;
  }
}

@keyframes anchor-core-pulse {
  0%, 100% {
    filter: drop-shadow(0 0 6px rgba(0, 150, 255, 0.7));
  }
  50% {
    filter: drop-shadow(0 0 14px rgba(0, 220, 255, 1)) drop-shadow(0 0 22px rgba(0, 150, 255, 0.65));
  }
}
</style>
